import { assertNever } from '@varos/util-typescript';
function parseTiptapMarksToAnnotationsAndHref(marks) {
    const annotations = {
        bold: false,
        italic: false,
        underline: false,
    };
    let href = null;
    if (marks) {
        for (const mark of marks) {
            switch (mark.type) {
                case 'bold':
                    annotations.bold = true;
                    break;
                case 'italic':
                    annotations.italic = true;
                    break;
                case 'underline':
                    annotations.underline = true;
                    break;
                case 'link':
                    if (mark.attrs?.href) {
                        href = mark.attrs.href;
                    }
                    break;
            }
        }
    }
    return { annotations, href };
}
function parseTiptapContentToCoreSpans(tiptapContent) {
    if (!tiptapContent) {
        return [];
    }
    const coreSpans = [];
    for (const item of tiptapContent) {
        if (item.type === 'text') {
            const textNode = item;
            if (textNode.text && textNode.text.length > 0) {
                const { annotations, href } = parseTiptapMarksToAnnotationsAndHref(textNode.marks);
                const textSpan = {
                    kind: 'text',
                    text: textNode.text,
                    annotations: annotations,
                    href: href,
                };
                coreSpans.push(textSpan);
            }
        }
        else if (item.type === 'mention') {
            const mentionNode = item;
            const mentionSpanData = mentionNode.attrs.item;
            if (mentionSpanData && mentionSpanData.kind === 'mention') {
                coreSpans.push(mentionSpanData);
            }
            else {
                console.warn('Invalid or missing mention data in TiptapMentionNode:', mentionNode);
                const fallbackId = mentionSpanData?.mention?.id || 'unknown';
                const fallbackText = mentionSpanData?.text || `@${fallbackId}`;
                coreSpans.push({
                    kind: 'text',
                    text: `[Invalid Mention: ${fallbackText}]`,
                    annotations: { bold: false, italic: false, underline: false },
                    href: null,
                });
            }
        }
        else {
            console.warn('Unsupported Tiptap node type in content array for spans (expected text or mention):', item.type, item);
            coreSpans.push({
                kind: 'text',
                text: `[Unsupported inline content: ${item.type}]`,
                annotations: { bold: false, italic: false, underline: false },
                href: null,
            });
        }
    }
    return coreSpans;
}
function parseTiptapNodeToCoreBlock(tiptapNode, listStyleForListItem) {
    if (tiptapNode.type === 'textBlock') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-textBlock-${Math.random().toString(36).substr(2, 9)}`;
        return {
            id: idToUse,
            kind: 'text',
            content: parseTiptapContentToCoreSpans(tiptapNode.content),
        };
    }
    if (tiptapNode.type === 'headingBlock') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-headingBlock-${Math.random().toString(36).substr(2, 9)}`;
        let level = tiptapNode.attrs?.level || 1;
        if (level < 1)
            level = 1;
        else if (level > 3)
            level = 3;
        return {
            id: idToUse,
            kind: 'heading',
            level: level,
            content: parseTiptapContentToCoreSpans(tiptapNode.content),
        };
    }
    if (tiptapNode.type === 'listItem') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-listItem-${Math.random().toString(36).substr(2, 9)}`;
        if (!listStyleForListItem) {
            console.warn(`ListItem (ID: ${idToUse}) processed without a listStyle. Defaulting to bullet.`);
        }
        const styleToUse = listStyleForListItem || 'bullet';
        let listItemSpans = [];
        if (tiptapNode.content && tiptapNode.content.length > 0) {
            const firstChild = tiptapNode.content[0];
            if (firstChild) {
                if (firstChild.type === 'paragraph') {
                    listItemSpans = parseTiptapContentToCoreSpans(firstChild.content);
                }
                else if (firstChild.type !== 'text' &&
                    firstChild.type !== 'mention' &&
                    firstChild.content) {
                    console.warn(`ListItem (ID: ${idToUse}) first child is not a paragraph (type: ${firstChild.type}). Parsing its content directly for spans.`);
                    listItemSpans = parseTiptapContentToCoreSpans(firstChild.content);
                }
                else {
                    console.warn(`ListItem (ID: ${idToUse}) first child is not a paragraph (type: ${firstChild.type}) or has no parsable content. Treating list item as empty or parsing its direct content if any.`);
                    listItemSpans = parseTiptapContentToCoreSpans(tiptapNode.content);
                }
            }
        }
        return {
            id: idToUse,
            kind: 'list_item',
            style: styleToUse,
            content: listItemSpans,
        };
    }
    if (tiptapNode.type === 'orderedList' || tiptapNode.type === 'bulletList') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-list-${Math.random().toString(36).substr(2, 9)}`;
        const coreListItems = [];
        const listStyle = tiptapNode.type === 'orderedList' ? 'number' : 'bullet';
        for (const itemNode of tiptapNode.content) {
            const coreListItem = parseTiptapNodeToCoreBlock(itemNode, listStyle);
            if (!Array.isArray(coreListItem)) {
                coreListItems.push(coreListItem);
            }
            else {
                console.warn('ListItem parsing unexpectedly returned an array:', coreListItem);
            }
        }
        return coreListItems;
    }
    if (tiptapNode.type === 'dividerBlock') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-dividerBlock-${Math.random().toString(36).substr(2, 9)}`;
        return {
            id: idToUse,
            kind: 'divider',
        };
    }
    if (tiptapNode.type === 'calloutBlock') {
        const idToUse = tiptapNode.attrs?.blockId ||
            `gen-calloutBlock-${Math.random().toString(36).substr(2, 9)}`;
        const iconAttr = tiptapNode.attrs?.icon;
        return {
            id: idToUse,
            kind: 'callout',
            icon: iconAttr === '' ? null : iconAttr || null,
            content: parseTiptapContentToCoreSpans(tiptapNode.content),
        };
    }
    if (tiptapNode.type === 'customBlock') {
        // tiptapNode.attrs is TiptapCustomBlockNodeAttrs, which now includes optional blockId
        const customNodeFromCore = tiptapNode.attrs?.node;
        const idFromDirectAttrs = tiptapNode.attrs?.blockId; // Directly access optional blockId
        const idToUse = customNodeFromCore?.id ||
            idFromDirectAttrs ||
            `gen-customBlock-${Math.random().toString(36).substr(2, 9)}`;
        if (customNodeFromCore && customNodeFromCore.kind === 'custom') {
            // Ensure the core CustomNode has both id and blockId set to idToUse.
            // The rest of the properties from customNodeFromCore (like blockType and other props) are spread.
            return { ...customNodeFromCore, id: idToUse, blockId: idToUse };
        }
        // Fallback for invalid custom node data
        console.warn('Invalid or missing custom node data in Tiptap customBlock node:', tiptapNode);
        // For the fallback block, its ID should be the Tiptap customBlock's own blockId if available,
        // or a newly generated one. Do NOT use an ID from an invalid `customNodeFromCore`.
        const fallbackId = idFromDirectAttrs ||
            `gen-fallback-customBlock-${Math.random().toString(36).substr(2, 9)}`;
        return {
            id: fallbackId,
            kind: 'text',
            content: [
                {
                    kind: 'text',
                    text: '[Invalid Custom Block]',
                    annotations: { bold: false, italic: false, underline: false },
                    href: null,
                },
            ],
        };
    }
    if (tiptapNode.type === 'paragraph') {
        throw new Error(`Tiptap 'paragraph' node unexpectedly reached parseTiptapNodeToCoreBlock. Node ID: ${tiptapNode.attrs?.blockId || 'unknown'}. Top-level paragraphs should be handled by the caller.`);
    }
    if (tiptapNode.type === 'mention') {
        console.error('TiptapMentionNode was passed to parseTiptapNodeToCoreBlock. This is unexpected as mentions are inline.', tiptapNode);
        const idToUse = `gen-mention-block-${Math.random().toString(36).substr(2, 9)}`;
        const mentionText = tiptapNode.attrs?.item?.text ||
            tiptapNode.attrs?.item?.mention?.id ||
            'mention';
        return {
            id: idToUse,
            kind: 'text',
            content: [
                {
                    kind: 'text',
                    text: `[Block Mention Error: ${mentionText}]`,
                    annotations: { bold: false, italic: false, underline: false },
                    href: null,
                },
            ],
        };
    }
    return assertNever(tiptapNode);
}
export function parseTiptapDocToEditorState(tiptapDoc) {
    const coreNodes = [];
    for (const item of tiptapDoc.content) {
        if (item.type === 'text') {
            console.warn('Root-level TiptapTextNode encountered. Wrapping in a TextBlockNode:', item);
            const idToUse = `gen-text-wrap-${Math.random().toString(36).substr(2, 9)}`;
            const textBlock = {
                id: idToUse,
                kind: 'text',
                content: parseTiptapContentToCoreSpans([item]),
            };
            coreNodes.push(textBlock);
        }
        else if (item.type === 'mention') {
            console.warn('Root-level TiptapMentionNode encountered. Wrapping in a TextBlockNode:', item);
            const idToUse = `gen-mention-wrap-${Math.random().toString(36).substr(2, 9)}`;
            const textBlock = {
                id: idToUse,
                kind: 'text',
                content: parseTiptapContentToCoreSpans([item]),
            };
            coreNodes.push(textBlock);
        }
        else if (item.type === 'paragraph') {
            throw new Error(`Top-level Tiptap 'paragraph' node encountered. This is not allowed. Node ID: ${item.attrs?.blockId || 'unknown'}`);
        }
        else {
            const parsedResult = parseTiptapNodeToCoreBlock(item);
            if (Array.isArray(parsedResult)) {
                coreNodes.push(...parsedResult);
            }
            else if (parsedResult) {
                coreNodes.push(parsedResult);
            }
        }
    }
    return {
        nodes: coreNodes,
    };
}
//# sourceMappingURL=printHelper.js.map